<!--
  @fileoverview Resend Message Button Component

  Displays an error icon with tooltip for failed messages.
  Clicking the icon triggers a resend attempt.

  @component ResendMessageButton
  @displayName Resend Button

  Features:
  - Error icon indicator for failed messages
  - Tooltip showing error details
  - Click to resend functionality
  - Disabled state to prevent spam

  @example
  <ResendMessageButton
    :isVisible="true"
    :resendMessage="handleResend"
    :isDisabled="false"
    :errorMessage="'Failed to send message'"
  />

  @vue3migration
  - Converted to script setup pattern
  - No store access needed for this component
-->
<template>
  <el-tooltip class="resend-tooltip" :content="errorMessage" placement="bottom">
    <el-icon
      :class="{ '!hidden': !isVisible, '!inline': isVisible }"
      :disabled="buttonDisabled"
      class="error-icon"
      @click="resendMessage()"
    ><Warning /></el-icon>
  </el-tooltip>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/ResendMessageButton
 * @description Error indicator and resend button for failed messages
 */
import { computed } from "vue";
import { Warning } from "@element-plus/icons-vue";
import { FAILED_MESSAGE_ERROR_TEXT } from "@/utils";

/**
 * Component props
 */
const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true,
  },
  resendMessage: {
    type: Function,
    required: true,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
});

/**
 * Check if button should be disabled
 *
 * Button is disabled if:
 * - isDisabled prop is true, OR
 * - Error message is not the standard failed message text
 *   (prevents resending for non-recoverable errors)
 */
const buttonDisabled = computed(() => {
  return props.isDisabled || props.errorMessage !== FAILED_MESSAGE_ERROR_TEXT;
});
</script>

<style scoped>
.resend-tooltip {
  padding: 0;
  border: none;
}

.error-icon {
  color: #ff392e;
  background-color: white;
  border-radius: 50%;
  cursor: pointer;
  margin: 0 5px 10px -10px;
}

.error-icon::before {
  font-size: 18px;
}
</style>
